<template>
    <div class="budget-followup-cph">
        <div v-if="data">

            <div class="graph-modal" v-if="graphModal">
                <div class="inner">
                    <div class="inner__title">
                        Udvikling i DB pr. time for "{{ graphModal.name }}"
                    </div>
                    <div>
                        <vue-apex-charts
                            :height="400"
                            :series="graphModal.graph"
                            :options="graphOptions"
                        />
                    </div>
                    <div class="close-wrapper">
                        <button class="budget-btn" @click="graphModal = null">
                            Luk
                        </button>
                    </div>
                </div>
            </div>

            <div class="placeholder">
                <div class="placeholder__heading">
                    <d-tooltip identifier="budgetfollowup.cph" placement="left" />
                    Opgavetyper
                </div>
                <div class="placeholder padding">
                    <div style="margin-bottom:30px;">
                        <div class="row">
                            <div class="col-lg-3" style="text-align:center;">
                                <div style="font-size:24px;font-weight:bold;">{{ data.budget.ebitda_goal | currency }}</div>
                                Ønsket EBITDA<br>(driftsoverskud)
                            </div>
                            <div class="col-lg-3" style="text-align:center;">
                                <div class="color-avg" style="font-size:24px;font-weight:bold;">{{ data.budget.cph_zero_revenue | currency }}</div>
                                Krav til DB pr. time for at nå nulpunktsomsætning
                            </div>
                            <div class="col-lg-3" style="text-align:center;">
                                <div class="color-good" style="font-size:24px;font-weight:bold;">{{ data.budget.cph_goal_ebitda | currency }}</div>
                                Krav til DB pr. time for at nå ønsket EBITDA
                            </div>
                            <div class="col-lg-3" style="text-align:center;">
                                <div style="font-size:24px;font-weight:bold;" :class="{'color-bad': data.total_coverage_per_hour < data.budget.cph_zero_revenue, 'color-avg': data.total_coverage_per_hour < data.budget.cph_goal_ebitda, 'color-good': data.total_coverage_per_hour > data.budget.cph_goal_ebitda}">{{ data.total_coverage_per_hour | currency }}</div>
                                DB pr. time<br>(realiseret)
                            </div>
                        </div>
                    </div>
                    <table style="width:100%;">
                        <thead>
                            <tr>
                                <th align="left">Opgavetype</th>
                                <th align="right" width="15%">Antal timer</th>
                                <th align="right" width="15%">Antal opgaver</th>
                                <th align="right" width="15%">Realiseret</th>
                                <th align="left" width="5%"><span style="opacity:0.5;">&nbsp;Udv.</span></th>
                                <th align="right" width="15%">Budget</th>
                                <th align="right" width="15%">Difference</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(projectType, index) in data.project_types" :key="'cphProjectType_' + index">
                                <td align="left" class="table-column__description" :title="projectType.name">
                                    {{ projectType.name }}
                                </td>
                                <td align="right">{{ projectType.hours | number }}</td>
                                <td align="right">{{ projectType.projects_count | number }}</td>
                                <td align="right" style="font-weight:bold;">
                                    <span v-if="projectType.coverage_per_hour !== 0">
                                        <span style="display:flex;align-items:center;justify-content:flex-end;">
                                            <span v-if="projectType.coverage_per_hour > data.budget.cph_goal_ebitda" class="color-good">
                                                {{ projectType.coverage_per_hour | currency }}
                                            </span>
                                            <span v-else-if="projectType.coverage_per_hour > data.budget.cph_zero_revenue" class="color-avg">
                                                {{ projectType.coverage_per_hour | currency }}
                                            </span>
                                            <span v-else class="color-bad">
                                                {{ projectType.coverage_per_hour | currency }}
                                            </span>
                                        </span>
                                    </span>
                                    <span v-else>
                                        -
                                    </span>
                                </td>
                                <td align="left">
                                    <span v-if="projectType.coverage_per_hour !== 0">
                                        <span style="font-size:14px;">
                                            <i class="fas fa-arrow-up fa-fw color-good" v-if="projectType.direction === 'up'" />
                                            <i class="fas fa-arrow-down fa-fw color-bad" v-if="projectType.direction === 'down'" />
                                            <i class="fas fa-arrow-right fa-fw color-avg" v-if="projectType.direction === 'neutral'" />
                                        </span>
                                        <i class="fas fa-chart-bar fa-fw" @click="graphModal = projectType" />
                                    </span>
                                </td>
                                <td align="right" class="budget">
                                    {{ projectType.budget | currency }}
                                </td>
                                <td v-if="projectType.coverage_per_hour !== 0" align="right" :class="{'color-bad': (Math.round(projectType.coverage_per_hour) - Math.round(projectType.budget)) < 0, 'color-good': (Math.round(projectType.coverage_per_hour) - Math.round(projectType.budget)) > 0}">
                                    <span v-if="(Math.round(projectType.coverage_per_hour) - Math.round(projectType.budget)) > 0">+</span>{{ Math.round(projectType.coverage_per_hour) - Math.round(projectType.budget) | currency }}
                                </td>
                                <td v-else align="right">
                                    -
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div v-else>
            <box-loading />
        </div>
    </div>
</template>

<script>
import * as budgetService from '@/services/budgetService'
import VueApexCharts from 'vue-apexcharts'

export default {
    data () {
        return {
            data: null,
            graphModal: null,
            graphOptions: {
                labels: [],
                yaxis: {
                    labels: {
                        formatter: value => {
                            return this.$options.filters.currency(value)
                        }
                    },
                    title: {
                        text: this.ylabel
                    },
                    forceNiceScale: true,
                    min: 0
                },
                stroke: {
                    width: 3,
                    curve: 'smooth'
                },
                dataLabels: {
                    enabled: false
                },
                legend: {
                    offsetY: 10,
                    itemMargin: {
                        horizontal: 5,
                        vertical: 10
                    },
                    position: 'bottom'
                },
                colors: ['#FFA000', '#4CAF50', '#03A9F4', '#172b43'],
                chart: {
                    toolbar: {
                        show: false
                    }
                },
                tooltip: {
                    enabled: true,
                    shared: true,
                    followCursor: true
                }
            }
        }
    },
    mounted () {
        this.getData()
    },
    methods: {
        getData () {
            budgetService.cph({}, (response) => {
                if (response.data.project_types.length > 0) {
                    this.graphOptions.labels = response.data.project_types[0].labels
                }
                this.data = response.data
                console.log(response)
            }, (err) => {
                console.log(err)
            })
        }
    },
    components: {
        VueApexCharts
    }
}
</script>

<style lang="scss" scoped>
@import "../../../../styles/vars.scss";

.graph-modal {
    z-index: 9999;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;

    .inner {
        background: #fff;
        border-radius: $borderradius--main;
        padding: $margin--main;
        width: 800px;
        max-width: 80%;
        max-height: 80%;
        overflow-y: auto;

        &__title {
            font-size: $fontsize--large;
            font-weight: bold;
            margin-bottom: $margin--main;
        }

        .close-wrapper {
            margin-top: $margin--main * 2;
        }
    }
}
table {

    th {
        padding: 4px 4px;

        &:first-of-type {
            padding-left: 0px;
        }
    }

    td {
        padding: 4px 4px;
        border-top: 1px solid #eee;

        i.fa-chart-bar {
            opacity: 0.5;
            cursor: pointer;
            transition: 0.3s ease all;

            &:hover {
                opacity: 1;
            }
        }

        &:first-of-type {
            padding-left: 0px;
        }

        &.budget {
            padding: 4px;
            // padding: 0px 0px;
            width: 220px;

            input {
                background: rgba(23,43,67,0.05);
                border-radius: 4px !important;
                padding: 4px !important;
                border-top: none !important;
                border-bottom: none !important;
                border: none !important;
                // border-radius: 0px !important;
                height: 100%;
                width: 200px !important;
                text-align: right;
                width: 100px;
                color: inherit !important;
                font-weight: inherit !important;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// budgetCph.vue?657eb59a